import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { motion, AnimatePresence } from 'framer-motion'
import { Plus, Edit, Trash2, Award, X, Upload, Image as ImageIcon, AlertCircle, Flame, Wind, Droplets } from 'lucide-react'
import { supabase, uploadFile, getPublicUrl } from '@/lib/supabase'
import { cn } from '@/lib/utils'
import type { Certification, CertificateCategory } from '@/types'

const certSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  category: z.enum(['fire', 'air', 'water']),
  description: z.string().optional(),
  sort_order: z.number(),
})

type CertFormData = z.infer<typeof certSchema>

const categoryOptions = [
  { value: 'fire', label: 'Fire', icon: Flame, color: 'text-red-600 bg-red-100' },
  { value: 'air', label: 'Air', icon: Wind, color: 'text-sky-600 bg-sky-100' },
  { value: 'water', label: 'Water', icon: Droplets, color: 'text-blue-600 bg-blue-100' },
] as const

export function CertificatesPage() {
  const [editingCert, setEditingCert] = useState<Certification | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const queryClient = useQueryClient()

  const { data: certificates, isLoading } = useQuery({
    queryKey: ['admin-certificates', categoryFilter],
    queryFn: async () => {
      let query = supabase.from('certifications').select('*').order('sort_order')
      if (categoryFilter !== 'all') query = query.eq('category', categoryFilter)
      const { data, error } = await query
      if (error) throw error
      return data as Certification[]
    },
  })

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<CertFormData>({
    resolver: zodResolver(certSchema),
    defaultValues: { name: '', category: 'fire', description: '', sort_order: 0 },
  })

  const selectedCategory = watch('category')

  const openModal = (cert?: Certification) => {
    if (cert) {
      setEditingCert(cert)
      reset({
        name: cert.name,
        category: cert.category,
        description: cert.description || '',
        sort_order: cert.sort_order,
      })
      setImagePreview(cert.image_url)
    } else {
      setEditingCert(null)
      reset({ name: '', category: 'fire', description: '', sort_order: certificates?.length || 0 })
      setImagePreview(null)
    }
    setImageFile(null)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingCert(null)
    setImageFile(null)
    setImagePreview(null)
  }

  const saveMutation = useMutation({
    mutationFn: async (data: CertFormData) => {
      let image_url = editingCert?.image_url || null
      if (imageFile) {
        const path = `certifications/${Date.now()}-${imageFile.name}`
        await uploadFile('certifications', path, imageFile)
        image_url = getPublicUrl('certifications', path)
      }

      if (editingCert) {
        await supabase.from('certifications').update({ ...data, image_url }).eq('id', editingCert.id)
      } else {
        await supabase.from('certifications').insert({ ...data, image_url })
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-certificates'] })
      closeModal()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await supabase.from('certifications').delete().eq('id', id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-certificates'] })
    },
  })

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setImageFile(file)
      setImagePreview(URL.createObjectURL(file))
    }
  }

  const removeImage = () => {
    setImageFile(null)
    setImagePreview(null)
  }

  const getCategoryIcon = (category: CertificateCategory) => {
    switch (category) {
      case 'fire': return <Flame className="w-4 h-4" />
      case 'air': return <Wind className="w-4 h-4" />
      case 'water': return <Droplets className="w-4 h-4" />
    }
  }

  const getCategoryColor = (category: CertificateCategory) => {
    switch (category) {
      case 'fire': return 'bg-red-100 text-red-700'
      case 'air': return 'bg-sky-100 text-sky-700'
      case 'water': return 'bg-blue-100 text-blue-700'
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Certificates</h1>
          <p className="text-secondary-600">Manage certification documents</p>
        </div>
        <button onClick={() => openModal()} className="btn-primary">
          <Plus className="w-5 h-5 mr-2" />
          Add Certificate
        </button>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {['all', 'fire', 'air', 'water'].map((cat) => (
          <button
            key={cat}
            onClick={() => setCategoryFilter(cat)}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize flex items-center gap-2',
              categoryFilter === cat
                ? 'bg-primary-600 text-white'
                : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
            )}
          >
            {cat !== 'all' && getCategoryIcon(cat as CertificateCategory)}
            {cat}
          </button>
        ))}
      </div>

      <div className="card">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
          </div>
        ) : certificates && certificates.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
            {certificates.map((cert) => (
              <div key={cert.id} className="border border-secondary-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
                <div className="aspect-[3/2] bg-secondary-100">
                  {cert.image_url ? (
                    <img src={cert.image_url} alt={cert.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Award className="w-12 h-12 text-secondary-400" />
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <span className={cn('badge text-xs mb-2 inline-flex items-center gap-1', getCategoryColor(cert.category))}>
                    {getCategoryIcon(cert.category)}
                    {cert.category}
                  </span>
                  <h3 className="font-semibold text-secondary-900">{cert.name}</h3>
                  {cert.description && (
                    <p className="text-sm text-secondary-500 mt-1 line-clamp-2">{cert.description}</p>
                  )}
                  <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-secondary-100">
                    <button
                      onClick={() => openModal(cert)}
                      className="p-2 text-secondary-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => confirm('Delete?') && deleteMutation.mutate(cert.id)}
                      className="p-2 text-secondary-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <Award className="w-12 h-12 text-secondary-300 mx-auto mb-4" />
            <p className="text-secondary-500">No certificates found</p>
          </div>
        )}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-4 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-xl bg-white rounded-2xl shadow-2xl z-50 flex flex-col max-h-[90vh]"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Award className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-secondary-900">
                      {editingCert ? 'Edit Certificate' : 'New Certificate'}
                    </h2>
                    <p className="text-sm text-secondary-500">
                      {editingCert ? 'Update certificate details' : 'Add a new certification'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeModal}
                  className="p-2 hover:bg-secondary-100 rounded-xl transition-colors"
                >
                  <X className="w-5 h-5 text-secondary-500" />
                </button>
              </div>

              {/* Modal Body */}
              <form onSubmit={handleSubmit((data) => saveMutation.mutate(data))} className="flex-1 overflow-y-auto">
                <div className="p-6 space-y-5">
                  {/* Name Field */}
                  <div className="form-group">
                    <label className="label">
                      Certificate Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      {...register('name')}
                      placeholder="e.g. DNV GL, Lloyd's Register"
                      className={cn('input', errors.name && 'input-error')}
                    />
                    {errors.name && (
                      <p className="error-text">
                        <AlertCircle className="w-4 h-4" />
                        {errors.name.message}
                      </p>
                    )}
                  </div>

                  {/* Category Selection */}
                  <div className="form-group">
                    <label className="label">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {categoryOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setValue('category', option.value)}
                          className={cn(
                            'flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all',
                            selectedCategory === option.value
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-secondary-200 hover:border-secondary-300 bg-white'
                          )}
                        >
                          <div className={cn(
                            'w-10 h-10 rounded-lg flex items-center justify-center',
                            option.color
                          )}>
                            <option.icon className="w-5 h-5" />
                          </div>
                          <span className={cn(
                            'text-sm font-medium',
                            selectedCategory === option.value ? 'text-primary-700' : 'text-secondary-600'
                          )}>
                            {option.label}
                          </span>
                        </button>
                      ))}
                    </div>
                    <input type="hidden" {...register('category')} />
                  </div>

                  {/* Description Field */}
                  <div className="form-group">
                    <label className="label">Description</label>
                    <textarea
                      {...register('description')}
                      rows={3}
                      placeholder="Brief description of this certification..."
                      className="input resize-none"
                    />
                  </div>

                  {/* Image Upload */}
                  <div className="form-group">
                    <label className="label">Certificate Image</label>
                    {imagePreview ? (
                      <div className="flex items-start gap-4">
                        <div className="image-preview">
                          <img src={imagePreview} alt="Preview" />
                          <button
                            type="button"
                            onClick={removeImage}
                            className="image-preview-remove"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-secondary-600 mb-2">Image uploaded</p>
                          <label className="btn-outline text-sm cursor-pointer">
                            <Upload className="w-4 h-4 mr-2" />
                            Change Image
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleImageChange}
                              className="sr-only"
                            />
                          </label>
                        </div>
                      </div>
                    ) : (
                      <label className="upload-area cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="sr-only"
                        />
                        <ImageIcon className="w-10 h-10 text-secondary-400 mb-3" />
                        <p className="text-sm font-medium text-secondary-600">
                          Click to upload image
                        </p>
                        <p className="text-xs text-secondary-400 mt-1">
                          PNG, JPG up to 5MB
                        </p>
                      </label>
                    )}
                  </div>

                  {/* Sort Order */}
                  <div className="form-group">
                    <label className="label">Display Order</label>
                    <input
                      type="number"
                      {...register('sort_order', { valueAsNumber: true })}
                      className="input w-32"
                      min={0}
                    />
                    <p className="text-xs text-secondary-400 mt-1">
                      Lower numbers appear first
                    </p>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-secondary-100 bg-secondary-50">
                  <button type="button" onClick={closeModal} className="btn-outline">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saveMutation.isPending}
                    className="btn-primary min-w-[120px]"
                  >
                    {saveMutation.isPending ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      editingCert ? 'Save Changes' : 'Create Certificate'
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
